import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "reactstrap";
import "./certificatesCard.scss";

const ISSUE_LABELS = {
  "self-signed": "Self-signed",
  "untrusted-chain": "Untrusted chain",
  "hostname-mismatch": "Name mismatch",
  "weak-tls": "Weak TLS",
  "weak-key": "Weak key",
};

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 3 * 60 * 1000;
const NAMES_COLLAPSED = 3;

function daysText(days) {
  if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
  if (days === 0) return "Expires today";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

function daysTone(days) {
  if (days < 0 || days <= 7) return "danger";
  if (days <= 30) return "warning";
  return "ok";
}

function formatTime(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
}

/** Names (SANs) a certificate is valid for; collapsed to a few until expanded. */
const NamesCell = ({ sans = [], total = 0 }) => {
  const [open, setOpen] = useState(false);
  if (!sans.length) return <span className="asm-certs-muted">—</span>;
  const shown = open ? sans : sans.slice(0, NAMES_COLLAPSED);
  const hidden = Math.max(total, sans.length) - shown.length;
  return (
    <div className="asm-certs-names">
      {shown.map((n) => (
        <div key={n}>{n}</div>
      ))}
      {(hidden > 0 || open) && (
        <button type="button" className="asm-certs-link" onClick={() => setOpen(!open)}>
          {open ? "Show fewer" : `+${hidden} more`}
        </button>
      )}
    </div>
  );
};

/** Popup: every certificate with its names, plus names certificates list that are not in the asset list. */
const CertificatesDetails = ({ isOpen, onClose, token, apiBase, theme }) => {
  const [detail, setDetail] = useState({ loading: true, items: [], unknownNames: [], error: null });

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setDetail({ loading: true, items: [], unknownNames: [], error: null });
    fetch(`${apiBase}certificates?limit=500&names=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (cancelled) return;
        if (body && body.status) {
          setDetail({ loading: false, items: body.items || [], unknownNames: body.unknownNames || [], error: null });
        } else {
          setDetail({ loading: false, items: [], unknownNames: [], error: "Could not load certificates." });
        }
      })
      .catch(() => {
        if (!cancelled) setDetail({ loading: false, items: [], unknownNames: [], error: "Could not load certificates." });
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, token, apiBase]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered scrollable contentClassName="asm-certs-modal-shell">
      {/* The popup renders outside the dashboard, so it carries its own theme and colours */}
      <div className="asm-certs-modal" data-theme={theme}>
        <div className="asm-certs-modal-head">
          <h5>Certificates</h5>
          <button type="button" className="asm-certs-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {detail.loading ? (
          <div className="asm-certs-empty">Loading…</div>
        ) : detail.error ? (
          <div className="asm-certs-empty">{detail.error}</div>
        ) : (
          <>
            <div className="asm-certs-table-wrap">
              <table className="asm-certs-table">
                <thead>
                  <tr>
                    <th>Host</th>
                    <th>Issuer</th>
                    <th>Expires</th>
                    <th>TLS</th>
                    <th>Issues</th>
                    <th>Names in certificate</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items.map((c) => {
                    const flags = c.issues.filter((i) => ISSUE_LABELS[i.key]);
                    return (
                      <tr key={`${c.host}:${c.port}`}>
                        <td className="asm-certs-host">{c.host}</td>
                        <td>{c.issuer || "Unknown"}</td>
                        <td>
                          <div>{formatDate(c.validTo)}</div>
                          <span className={`asm-certs-days is-${daysTone(c.daysLeft)}`}>{daysText(c.daysLeft)}</span>
                        </td>
                        <td>{c.protocol || "—"}</td>
                        <td>
                          {flags.length === 0 && c.daysLeft >= 0 ? (
                            <span className="asm-certs-muted">None</span>
                          ) : (
                            <>
                              {c.daysLeft < 0 && <span className="asm-certs-flag is-danger">Expired</span>}
                              {flags.map((i) => (
                                <span className="asm-certs-flag" key={i.key}>
                                  {ISSUE_LABELS[i.key]}
                                </span>
                              ))}
                            </>
                          )}
                        </td>
                        <td>
                          <NamesCell sans={c.sans} total={c.sanCount} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="asm-certs-unknown">
              <div className="asm-certs-unknown-title">Names found in certificates but not in your assets</div>
              {detail.unknownNames.length === 0 ? (
                <div className="asm-certs-muted">None. Every name in your certificates is already a known asset.</div>
              ) : (
                <>
                  <ul>
                    {detail.unknownNames.map((u) => (
                      <li key={u.name}>
                        <span className="asm-certs-host">{u.name}</span>
                        <span className="asm-certs-muted"> · seen on {u.seenOn}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="asm-certs-muted">
                    These names are only listed here. They are not added to your assets automatically.
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

/**
 * Dashboard card: TLS certificates of the organization's hosts, soonest expiry first.
 * Certificates are checked once a day; "Scan now" starts a check right away (at most once an hour).
 */
const CertificatesCard = ({ theme = "light" }) => {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const pollTimer = useRef(null);
  const pollStarted = useRef(0);

  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase().trim();
  const canScan = ["tenant_admin", "super_admin", "analyst"].includes(role);
  const apiBase = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";

  const load = useCallback(async () => {
    if (!token) return null;
    try {
      const res = await fetch(`${apiBase}certificates?limit=6`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;
      const body = await res.json();
      if (body && body.status) {
        setData(body);
        return body;
      }
    } catch (e) {
      // The card simply stays hidden if the server cannot be reached
    }
    return null;
  }, [token, apiBase]);

  const stopPolling = useCallback(() => {
    clearTimeout(pollTimer.current);
    pollTimer.current = null;
  }, []);

  const poll = useCallback(async () => {
    const body = await load();
    if (body && !body.scanning) {
      setScanning(false);
      setMessage(null);
      return;
    }
    if (Date.now() - pollStarted.current > POLL_MAX_MS) {
      setScanning(false);
      return;
    }
    pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, [load]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollStarted.current = Date.now();
    pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, [poll, stopPolling]);

  useEffect(() => {
    load().then((body) => {
      if (body && body.scanning) {
        setScanning(true);
        startPolling();
      }
    });
    return stopPolling;
  }, [load, startPolling, stopPolling]);

  const handleScan = async () => {
    setMessage(null);
    setScanning(true);
    try {
      const res = await fetch(`${apiBase}certificates/scan`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 202 || res.status === 409) {
        startPolling();
        return;
      }
      setScanning(false);
      if (res.status === 429) {
        const when = body.nextScanAllowedAt ? formatTime(body.nextScanAllowedAt) : "";
        setMessage(`Certificates were checked recently.${when ? ` You can scan again after ${when}.` : ""}`);
      } else {
        setMessage(body.error || "Could not start the certificate check.");
      }
    } catch (e) {
      setScanning(false);
      setMessage("Could not start the certificate check.");
    }
  };

  if (!data) return null;

  const { summary, items } = data;
  const nextAllowed = data.nextScanAllowedAt ? new Date(data.nextScanAllowedAt) : null;
  const coolingDown = nextAllowed && nextAllowed > new Date();

  return (
    <div className="asm-card asm-certs-card">
      <div className="asm-card-header asm-certs-header">
        <span>Certificates</span>
        <span className="asm-certs-actions">
          {summary.total > 0 && (
            <button type="button" className="asm-certs-scan" onClick={() => setShowAll(true)}>
              View all
            </button>
          )}
          {canScan && (
            <button
              type="button"
              className="asm-certs-scan"
              onClick={handleScan}
              disabled={scanning || coolingDown}
              title={coolingDown ? `Checked recently. Available again after ${formatTime(nextAllowed)}` : "Check certificates now"}
            >
              {scanning ? "Checking…" : "Scan now"}
            </button>
          )}
        </span>
      </div>

      {message && <div className="asm-certs-message">{message}</div>}

      {summary.total === 0 ? (
        <div className="asm-certs-empty">
          {scanning
            ? "Checking certificates. This can take a minute."
            : "No certificates checked yet. They are checked once a day, or press Scan now."}
        </div>
      ) : (
        <>
          <div className="asm-certs-stats">
            <div className="asm-certs-stat">
              <span className="asm-certs-stat-value">{summary.total}</span>
              <span className="asm-certs-stat-label">Checked</span>
            </div>
            <div className="asm-certs-stat">
              <span className={`asm-certs-stat-value${summary.expired ? " is-danger" : ""}`}>{summary.expired}</span>
              <span className="asm-certs-stat-label">Expired</span>
            </div>
            <div className="asm-certs-stat">
              <span className={`asm-certs-stat-value${summary.expiring30 ? " is-warning" : ""}`}>{summary.expiring30}</span>
              <span className="asm-certs-stat-label">Expiring in 30 days</span>
            </div>
            <div className="asm-certs-stat">
              <span className="asm-certs-stat-value is-ok">{summary.healthy}</span>
              <span className="asm-certs-stat-label">Healthy</span>
            </div>
          </div>

          <ul className="asm-certs-list">
            {items.map((c) => {
              const flags = c.issues.filter((i) => ISSUE_LABELS[i.key]);
              return (
                <li className="asm-certs-item" key={`${c.host}:${c.port}`}>
                  <div className="asm-certs-main">
                    <div className="asm-certs-host">{c.host}</div>
                    <div className="asm-certs-meta">
                      {c.issuer || "Unknown issuer"}
                      {flags.map((i) => (
                        <span className="asm-certs-flag" key={i.key}>
                          {ISSUE_LABELS[i.key]}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className={`asm-certs-days is-${daysTone(c.daysLeft)}`}>{daysText(c.daysLeft)}</span>
                </li>
              );
            })}
          </ul>
          {summary.total > items.length && (
            <div className="asm-certs-footer">
              Showing the {items.length} certificates expiring soonest, of {summary.total}
            </div>
          )}
        </>
      )}

      <CertificatesDetails isOpen={showAll} onClose={() => setShowAll(false)} token={token} apiBase={apiBase} theme={theme} />
    </div>
  );
};

export default CertificatesCard;
